import { Button, useArtifactCanvas } from '@yeshuman/sdk'
import { Sparkles } from 'lucide-react'
import { useCallback } from 'react'

import { useImproveCompleteness, type ImproveTarget } from '../../hooks/use-improve'
import { opportunityCanvasSurface } from '../../lib/canvas-ui-surface'
import { ImproveReviewCanvas } from './improve-review-canvas'
import { formatStrength, missingLabelsPhrase } from './improve-missing-list'

/** Job workspace Details status strip: why matching treats this job as incomplete, with the repair entry. */
export function ImproveJobDescriptionStrip({
  opportunityId,
  updatedAt,
}: {
  opportunityId: string
  updatedAt?: string | null
}) {
  const target: ImproveTarget = { kind: 'opportunity', opportunityId }
  const { data } = useImproveCompleteness(target, updatedAt)
  const { openCanvas, closeCanvas } = useArtifactCanvas()

  const openReview = useCallback(() => {
    openCanvas({
      title: 'Improve job description',
      preventAutoFocus: true,
      surface: opportunityCanvasSurface(opportunityId, ['title', 'description', 'add_skills', 'remove_skills']),
      content: <ImproveReviewCanvas target={{ kind: 'opportunity', opportunityId }} onClose={closeCanvas} />,
    })
  }, [closeCanvas, openCanvas, opportunityId])

  if (!data || data.ok) return null

  return (
    <div
      className="flex items-start gap-2 rounded-md border border-border/60 bg-muted/40 px-3 py-2.5"
      role="status"
    >
      <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
      <div className="min-w-0 text-sm leading-snug text-muted-foreground">
        <span className="font-medium text-foreground">Matching sees an incomplete job</span>
        <span className="text-xs">
          {' — '}
          {formatStrength(data.score)} complete, missing {missingLabelsPhrase(data.missing)}.{' '}
        </span>
        <Button
          type="button"
          variant="link"
          size="sm"
          className="h-auto px-0 text-xs align-baseline"
          onClick={openReview}
        >
          Improve job description
        </Button>
      </div>
    </div>
  )
}
